/** Paramètres → Produits : stock, lignes, remplissage auto. */
import { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { CheckField } from "../../components/CheckField";
import { Field } from "../../components/Field";
import { useCompany } from "../../company/CompanyProvider";
import { productSettingsFromCompany, type CompanyProductSettings } from "../../products/settings";
import { isCompanyOwner } from "./owner";

export function ProductsTab() {
  const { company, updateProductSettings } = useCompany();
  const [draft, setDraft] = useState<CompanyProductSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!company) return;
    setDraft(productSettingsFromCompany(company));
    setMsg("");
    setErr("");
  }, [company]);

  if (!company || !draft) {
    return <p className="text-sm" style={{ color: "var(--fx-muted)" }}>Aucune société.</p>;
  }

  const owner = isCompanyOwner(company);

  function set<K extends keyof CompanyProductSettings>(key: K, value: CompanyProductSettings[K]) {
    setDraft((cur) => (cur ? { ...cur, [key]: value } : cur));
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      await updateProductSettings(company.id, draft);
      setMsg("Enregistré.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fx-co-card">
      <header className="fx-co-card__head">
        <h2>Produits</h2>
        <p>Stock, champs de ligne et remplissage — le catalogue est dans le menu Catalogue.</p>
        {!owner ? (
          <p className="fx-co-form__hint">Lecture seule — seul le propriétaire enregistre les produits.</p>
        ) : null}
      </header>
      <div className="fx-fmt">
        <div className="fx-fmt-row">
          <span>Stock</span>
          <div className="flex flex-col gap-2">
            <CheckField
              label="Gérer l’inventaire"
              checked={draft.pr_inventory}
              onChange={(next) => set("pr_inventory", next)}
            />
            <p className="fx-co-form__hint m-0">Quantité en stock, déduite à la validation de la facture — pas au brouillon.</p>
            <CheckField
              label="Prévenir par e-mail quand le stock atteint le seuil"
              checked={draft.pr_stock_mail}
              onChange={(next) => set("pr_stock_mail", next)}
            />
            <Field
              label="Seuil de notification"
              type="number"
              min={0}
              max={999999}
              value={String(draft.pr_stock_threshold)}
              onChange={(e) => set("pr_stock_threshold", Math.min(999999, Math.max(0, Number(e.target.value) || 0)))}
            />
          </div>
        </div>
        <div className="fx-fmt-row">
          <span>Lignes</span>
          <div className="flex flex-col gap-2">
            <CheckField
              label="Afficher une réduction sur la ligne"
              checked={draft.pr_show_discount}
              onChange={(next) => set("pr_show_discount", next)}
            />
            <CheckField
              label="Afficher le coût du produit (marge)"
              checked={draft.pr_show_cost}
              onChange={(next) => set("pr_show_cost", next)}
            />
            <p className="fx-co-form__hint m-0">Le coût reste interne — jamais sur le PDF client.</p>
            <CheckField
              label="Afficher la quantité"
              checked={draft.pr_show_qty}
              onChange={(next) => set("pr_show_qty", next)}
            />
            <p className="fx-co-form__hint m-0">Sinon on pose la quantité par défaut.</p>
            <Field
              label="Quantité par défaut"
              type="number"
              min={1}
              max={9999}
              value={String(draft.pr_default_qty)}
              onChange={(e) => set("pr_default_qty", Math.min(9999, Math.max(1, Number(e.target.value) || 1)))}
            />
          </div>
        </div>
        <div className="fx-fmt-row">
          <span>Catalogue</span>
          <div className="flex flex-col gap-2">
            <CheckField
              label="Remplir libellé et prix en choisissant un produit"
              checked={draft.pr_autofill}
              onChange={(next) => set("pr_autofill", next)}
            />
            <CheckField
              label="Mettre à jour la fiche produit depuis une facture"
              checked={draft.pr_sync_from_invoice}
              onChange={(next) => set("pr_sync_from_invoice", next)}
            />
            <p className="fx-co-form__hint m-0">
              Uniquement depuis un brouillon. Ça change le catalogue, pas les pièces déjà validées.
            </p>
          </div>
        </div>
      </div>
      <p className="fx-co-form__hint">Euro seulement — pas de conversion de prix vers une autre devise.</p>
      {err ? <p className="fx-co-form__err">{err}</p> : null}
      {msg ? <p className="fx-co-form__ok">{msg}</p> : null}
      <div className="fx-co-card__foot">
        <Button disabled={busy || !owner} onClick={() => void save()}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}
